"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import {
  DEFAULT_IMAGE,
  DEFAULT_IMAGE_ASPECT_RATIO,
  DEFAULT_IMAGE_MAX_WIDTH,
  IMAGE_FILE_TYPES,
} from "@/libs/builder/consts";
import { onImageChange } from "@/libs/builder/imageUtils";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

const ResponsiveImage = dynamic(async () =>
  withValidation(responsiveImageInputs, (await import("./ResponsiveImage")).ResponsiveImage, "ResponsiveImage")
);

export const IMAGE_INPUT = {
  name: "image",
  type: "file",
  allowedFileTypes: IMAGE_FILE_TYPES,
  defaultValue: DEFAULT_IMAGE,
  onChange: onImageChange,
} as const satisfies Inputs[number];

export const COMMON_IMAGE_INPUTS = [
  {
    name: "aspectRatio",
    type: "number",
    helperText:
      "This is the ratio of width/height, e.g. set to 1.5 for a 300px wide and 200px tall photo. This is used to reserve space for the image while it loads.",
    advanced: true,
  },
  {
    name: "height",
    type: "number",
    hideFromUI: true,
  },
  {
    name: "width",
    type: "number",
    hideFromUI: true,
  },
] as const satisfies Inputs;

export const OPTIONAL_IMAGE_INPUTS = [
  {
    ...IMAGE_INPUT,
    required: false,
  },
  ...COMMON_IMAGE_INPUTS,
] as const satisfies Inputs;

export const REQUIRED_IMAGE_INPUTS = [
  {
    ...IMAGE_INPUT,
    required: true,
  },
  ...COMMON_IMAGE_INPUTS,
] as const satisfies Inputs;

export const responsiveImageInputs = [
  {
    name: "desktopImage",
    type: "object",
    required: true,
    subFields: REQUIRED_IMAGE_INPUTS,
    defaultValue: {
      image: DEFAULT_IMAGE,
      aspectRatio: DEFAULT_IMAGE_ASPECT_RATIO,
      width: DEFAULT_IMAGE_MAX_WIDTH,
      height: DEFAULT_IMAGE_ASPECT_RATIO * DEFAULT_IMAGE_MAX_WIDTH,
    },
  },
  {
    name: "tabletImage",
    type: "object",
    required: true,
    subFields: OPTIONAL_IMAGE_INPUTS,
    defaultValue: {},
  },
  {
    name: "mobileImage",
    type: "object",
    required: true,
    subFields: OPTIONAL_IMAGE_INPUTS,
    defaultValue: {},
  },
  {
    name: "altText",
    type: "string",
    required: true,
    helperText: "Text to display when the user has images off",
    defaultValue: "Image alternative text",
  },
  {
    name: "title",
    type: "string",
    helperText: "Text to display when hovering over the asset",
  },
  {
    name: "highPriority",
    type: "boolean",
    advanced: true,
    helperText:
      "Mark this image as high priority compared to other images on the page. This prevents lazy loading of the image and tells the browser to load this image before others on the page.",
  },
  {
    name: "quality",
    type: "number",
    defaultValue: 80,
    max: 100,
    min: 1,
    helperText: "The quality of the image, from 1 to 100",
  },
] as const satisfies Inputs;

export const ResponsiveImageComponent: RegisteredComponent = {
  name: "ResponsiveImage",
  component: ResponsiveImage,
  inputs: responsiveImageInputs,
};
